// Optimized and fixed ClassesTable using Ant Design v5 and MUI v7

import React, { useCallback, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow } from '@mui/material';
import { Card, Col, Divider, Input, Modal as AntdModal, Row } from 'antd';
import { get, isEmpty, sortBy } from 'lodash';
import Loading from 'components/Loading';
import VocabAccessModal from 'components/admin/VocabPermission';
import { useCurrentOrg } from 'store/organizations/selectors';
import SpinLoader from 'components/Loader/SpinLoader';
import { handleGetClassesForOrg } from 'store/organizations/thunks';
import { useDispatch } from 'react-redux';

const BORDERCOLOR = ["rgb(200,109,152)", "rgb(146,158,227)", "rgb(80,233,200)", "rgb(238,206,151)"];

const Wrapper = styled.div`
  width: 100%;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-top: 30px;
  background-color: #fff;
`;

const Text = styled.p`
  color: black;
  font-size: 17px;
  font-weight: 400;
`;

const Label = styled.span`
  font-weight: 600;
`;

const Value = styled.span`
  font-size: 17px;
  font-weight: 500;
  cursor: pointer;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
`;

const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
`;

const OverviewBlock = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const ClassesTable = ({ userloading , onTabsJump = () => null  , active_tab}) => {

  const [filteredClasses, setFilteredClasses] = useState([]);
  const [pageInfo, setPageInfo] = useState({ page: 0, rowsPerPage: 100 });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [data_loading, setDataLoading] = useState(false);
  const [total_count, setToatalCount] = useState(0);
  const [classes_list, setClass_list] = useState([])
  const [searchText, setSearchText] = useState('');
  const org = useCurrentOrg()
  const dispatch = useDispatch()
  const prevOrgIdRef = useRef(org?.id);

  async function getInfo() {
    try {
      setDataLoading(true)
      const response = await dispatch(handleGetClassesForOrg(pageInfo.page*pageInfo.rowsPerPage, pageInfo.rowsPerPage, org.id))
      if (response && response.success) {
        const { total_count, classes: classes_lst } = response.data || {};
        setToatalCount(total_count)
        setClass_list(classes_lst)
      }
    } catch (error) {

    } finally {
      setDataLoading(false)
    }

  }
  useEffect(() => {
    if (org && org.id) {
      if (active_tab != "see_classes") return;
      getInfo()
    }
  }, [org, pageInfo , active_tab])


  useEffect(() => {
    const sorted = sortBy(classes_list, ["displayName"]);
    setFilteredClasses(sorted);
  }, [classes_list]);

  useEffect(() => {
    if (active_tab !== "see_classes") return;
    if (prevOrgIdRef.current !== undefined && prevOrgIdRef.current !== org?.id) {
      setSearchText('');
    }
    prevOrgIdRef.current = org?.id;
  }, [org?.id, active_tab])

  useEffect(() => {
    if (!searchText) {
      const sorted = sortBy(classes_list, ["displayName"]);
      setFilteredClasses(sorted);
    } else {
      const value = searchText.toLowerCase().replace(/\s+/g, '');
      const results = sortBy(
        classes_list.filter((x) =>
          String(x?.displayName ?? '')
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(value),
        ),
        ['displayName'],
      );
      setFilteredClasses(results);
    }
  }, [searchText, classes_list]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleRowClick = (row) => {
    setSelectedClass({
      ...row,
      teacherName: Array.isArray(row?.teachers)?row.teachers.join(","):'',
      schoolName: row.school_name || ''
    });
    setModalOpen(true);
  };

  const displayClasses = filteredClasses;
  const onPageChange = useCallback((_, newPage) => {
    setPageInfo((p) => ({ ...p, page: newPage }));
  }, []);

  const onRowsPerPageChange = useCallback((e) => {
    setPageInfo({ page: 0, rowsPerPage: parseInt(e.target.value, 10) });
  }, []);

  return (
    <Wrapper>
      <SpinLoader dataloader={data_loading} /> 
       <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
            <Text>
            View <Label>Class</Label> Records.
          </Text>
          <Input
            placeholder="Search Classes by name"
            allowClear
            value={searchText}
            onChange={handleSearch}
            style={{ width: 250 }}
          />
        </Row>
      <StyledTableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {['School Name', 'Name', 'Code', 'Primary Teacher', 'Student'].map(header => (
                <TableCell key={header} style={{ fontWeight: 600 }}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmpty(displayClasses) && (
              userloading ? (
                <TableRow><TableCell colSpan={5}><Loading size={30} /></TableCell></TableRow>
              ) : (
                <TableRow><TableCell colSpan={5} align="center">No results</TableCell></TableRow>
              )
            )}
            {!isEmpty(displayClasses) && displayClasses.map(row => {
              return (
                <TableRow key={row.id} hover onClick={() => handleRowClick(row)}>
                  <TableCell>{get(row, 'school_name', '-')}</TableCell>
                  <TableCell style={{whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 200}}>{row.displayName ?? '-'} {row.archived && <span style={{ color: BORDERCOLOR[3] }}>(Archived)</span>}</TableCell>
                  <TableCell>{row.code || '-'}</TableCell>
                  <TableCell style={{whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 200}}>{Array.isArray(row?.teachers)?row.teachers.join(","):''}</TableCell>
                  <TableCell style={{ color: BORDERCOLOR[0] }}>{row.active_student_count ?? '-'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={total_count}
                rowsPerPage={pageInfo.rowsPerPage}
                page={pageInfo.page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </StyledTableContainer>

      <AntdModal
        title={`${selectedClass?.displayName || ''} ${selectedClass?.demo ? '(Demo)' : ''}`}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        centered
        width="60%"
      >
        <ProfileRow>
          <VocabAccessModal id={selectedClass?.id} type="class" />
        </ProfileRow>
        <Card style={{ marginTop: 16 }} title="Class Info">
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <OverviewBlock>
                <InfoLabel>School Name</InfoLabel>
                <Value onClick={() => onTabsJump("see_schools", selectedClass)} style={{ color: 'rgb(20, 100, 255)' }}>{selectedClass?.schoolName}</Value>
              </OverviewBlock>
              <OverviewBlock>
                <InfoLabel>Name</InfoLabel>
                <Value>{selectedClass?.displayName}</Value>
              </OverviewBlock>
            </Col>
            <Col xs={24} lg={12}>
              <OverviewBlock>
                <InfoLabel>Primary Teacher</InfoLabel>
                <Value onClick={() => onTabsJump("see_teachers", selectedClass)} style={{ color: 'rgb(20, 100, 255)' }}>{selectedClass?.teacherName}</Value>
              </OverviewBlock>
              <OverviewBlock>
                <InfoLabel>Code</InfoLabel>
                <Value>{selectedClass?.code ?? '-'}</Value>
              </OverviewBlock>
            </Col>
          </Row>
          <Divider />
          <Row>
            <Col span={12}>
              <OverviewBlock>
                <InfoLabel>Students</InfoLabel>
                <Value onClick={() => onTabsJump("see_students", selectedClass)} style={{ color: 'rgb(20, 100, 255)' }}>{selectedClass?.studentCount ?? '-'}</Value>
              </OverviewBlock>
            </Col>
          </Row>
        </Card>
      </AntdModal>
    </Wrapper>
  );
};

export default ClassesTable;
